import Swal from 'sweetalert2';
import localVideoUploadServiceInstance from './LocalVideoUploadService';
import videoCRUDServiceInstance from './VideoCRUDService';
import { useVideoProcessing } from '../contexts/VideoProcessingContext';
import { ReturnCodes } from '../utils/constants';
/**
 * Helper service for video upload functionality
 * Consolidates the common upload logic used across different components
 */
class VideoUploadService {
  constructor() {
    this.trackingFunction = null;
  }

  // Method to be called from a component to set the tracking function
  setTrackingFunction(trackFn) {
    this.trackingFunction = trackFn;
  }

  /**
   * Handles video upload including validation and API interactions
   * @param {string} linkType - The type of link (GOOGLE_DRIVE, YOUTUBE, LOCAL_FILE)
   * @param {Object} params - Upload parameters
   * @returns {Promise} Promise resolving to upload result
   */
  async uploadVideo(linkType, params) {
    const {
      setFileUploading,
      googleLinkURL,
      youtubeLinkURL,
      videoName,
      folderPath,
      isCompressed,
      selectOpenAI,
      isLogging,
      skipTranslationIsChecked,
      onlyTransliterationIsChecked,
      bitRate,
      crfValue,
      setOpen,
      onSuccess,
      selectedFile
    } = params;

    setFileUploading(true);
    let url = '';
    
    if (linkType === 'GOOGLE_DRIVE') {
      url = googleLinkURL;
    } else if (linkType === 'YOUTUBE') {
      url = youtubeLinkURL;
    } else if (linkType === 'LOCAL_FILE') {
      try {
        url = await localVideoUploadServiceInstance.uploadFile(selectedFile);
        console.log("Local file upload response:", url); // Debug the response
        url = url?.data; // Assuming the response contains the file URL
      } catch (error) {
        console.error('Error uploading local file:', error);
        setFileUploading(false);
        return;
      }
    }

    try {
      if (videoName.length === 0) {
        setFileUploading(false);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Please enter a video name'
        });
        return;
      }

      return videoCRUDServiceInstance
        .addVideoInFolderWithPromise(
          linkType,
          url,
          JSON.parse(localStorage.getItem('authUser')),
          videoName,
          folderPath,
          isCompressed,
          selectOpenAI,
          isLogging,
          skipTranslationIsChecked,
          onlyTransliterationIsChecked,
          bitRate,
          crfValue
        )
        .then((result) => {
          setFileUploading(false);
          if (setOpen) setOpen(false);
          
          console.log("Upload result:", result); // Debug the response
          
          // Check for successful upload and video ID
          if (result && (result.status === "FILE_UPLOAD_SUCCESS" || result.id || result.videoId)) {
            const videoId = result.id || result.videoId || result._id;
            
            // Call tracking function if available and we have a videoId
            if (this.trackingFunction && videoId) {
              console.log("Tracking uploaded video:", videoId, videoName);
              this.trackingFunction(videoId, videoName + ' - New Video Upload', skipTranslationIsChecked);
            } else {
              console.warn("Cannot track video: ", 
                !this.trackingFunction ? "No tracking function available" : "No video ID returned");
            }
            
            // Show success message
            Swal.fire({
              icon: 'success',
              title: 'Video Uploaded Successfully',
              html: `Your video <strong>${videoName}</strong> is now being processed.<br><br>
               You can track its progress in the toast notifications in the bottom right corner.<br><br>
               For detailed status and progress information, visit the <a href="/status/processing-status" style="color: blue; text-decoration: underline;" target="_blank" rel="noopener noreferrer">Processing Status Dashboard</a>.`,
              timer: 5000
            });
            
            if (onSuccess && typeof onSuccess === 'function') {
              onSuccess();
            }
            return true;
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: 'Failed to upload video. Please try again.'
            });
            return false;
          }
        })
        .catch((error) => {
          console.error('Error in video upload:', error);
          setFileUploading(false);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to upload video'
          });
          return false;
        });
    } catch (err) {
      console.error('Exception in uploadVideo:', err);
      setFileUploading(false);
      return false;
    }
  }

  /**
   * Show detailed process information modal
   * @param {string} linkType - The type of link
   * @param {string} videoName - The name of the video
   * @param {boolean} skipTranslationIsChecked - Whether translation is skipped
   */
  showUploadProcessInfo(linkType, videoName, skipTranslationIsChecked) {
    Swal.fire({
      icon: 'info',
      title: 'Upload Process Information',
      html: `
        <p>Link Type: ${linkType}</p>
        <p>Video Name: ${videoName}</p>
        <p>Skip Translation: ${skipTranslationIsChecked}</p>
      `,
      showConfirmButton: true
    });
  }

  /**
   * Regenerates subtitles for a video
   * @param {string} videoId - The ID of the video
   * @param {string} videoName - The name of the video
   * @returns {Promise} Promise resolving to regeneration result
   */
  async regenerate(videoId, videoName) {
    const userData = JSON.parse(localStorage.getItem('authUser'));

    try {
      const result = await videoCRUDServiceInstance.regenerate(
        videoId, 
        userData.email, 
        userData.access
      );
      
      console.log("Regenerate result:", result);
      
      if (result && result.status === ReturnCodes.OK) {
        // Call tracking function if available and we have a videoId
        if (this.trackingFunction && videoId) {
          console.log("Tracking regenerated video:", videoId, videoName);
          this.trackingFunction(videoId, videoName + ' - Regenerate Subtitles', false);
        } else {
          console.warn("Cannot track video: ", 
            !this.trackingFunction ? "No tracking function available" : "No video ID returned");
        }
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Error in regeneration:', error);
      return false;
    }
  }

  /**
   * Performs text-to-text translation for a video
   * @param {string} videoId - The ID of the video
   * @param {string} videoName - The name of the video
   * @param {string} sourceLanguage - The source language
   * @param {Array} targetLanguages - Array of target languages
   * @param {boolean} skipTranslation - Whether to skip translation
   * @returns {Promise} Promise resolving to translation result
   */
  async text2Text(videoId, videoName, sourceLanguage, targetLanguages, skipTranslation) {
    const userData = JSON.parse(localStorage.getItem('authUser'));

    try {
      const result = await videoCRUDServiceInstance.text2Text(
        videoId,
        sourceLanguage,
        targetLanguages,
        skipTranslation,
        userData.email,
        userData.access
      );
      
      console.log("Text2Text result:", result);
      
      if (result && result.status === ReturnCodes.OK) {
        // Call tracking function if available and we have a videoId
        if (this.trackingFunction && videoId) {
          console.log("Tracking text2text for video:", videoId, videoName);
          this.trackingFunction(videoId, videoName + ' - Text 2 Text Translation', skipTranslation);
        } else {
          console.warn("Cannot track video: ", 
            !this.trackingFunction ? "No tracking function available" : "No video ID returned");
        }
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Error in text2Text:', error);
      return false;
    }
  }
}

export default new VideoUploadService();